import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import api from '@/lib/api';
import { extractErrorMessage } from '@/lib/api-errors';

// Module-level cache: tokens already submitted in this browser session.
// Survives React StrictMode's intentional double-mount in dev so the same
// single-use token isn't sent twice (which would yield a false-positive error).
const submittedTokens = new Set();

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('verifying'); // verifying, success, error, no-token
  const [message, setMessage] = useState('');
  const inFlight = useRef(false);

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('no-token');
        return;
      }
      // Hard guards against double-submit (StrictMode, hot reload, retry)
      if (inFlight.current) return;
      if (submittedTokens.has(token)) return;
      inFlight.current = true;
      submittedTokens.add(token);

      try {
        const response = await api.post('/auth/verify-email', { token });
        setStatus('success');
        setMessage(response.data.message);
      } catch (error) {
        setStatus('error');
        setMessage(extractErrorMessage(error, 'Erro ao verificar email'));
      } finally {
        inFlight.current = false;
      }
    };

    verifyEmail();
  }, [token]);

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <>
            <div className="w-20 h-20 bg-ruby/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-ruby animate-spin" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              A verificar o teu email...
            </h2>
            <p className="text-muted-foreground">
              Por favor, aguarda um momento.
            </p>
          </>
        );

      case 'success':
        return (
          <>
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              Email verificado!
            </h2>
            <p className="text-muted-foreground mb-8">
              {message || 'A tua conta está agora totalmente ativa. Bem-vindo ao Intimate.pt!'}
            </p>
            <Button
              onClick={() => navigate('/home')}
              data-testid="go-to-home"
              className="w-full bg-ruby hover:bg-ruby-light text-white rounded-full py-6 font-semibold"
            >
              Começar a explorar
            </Button>
          </>
        );

      case 'error':
        return (
          <>
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              Erro na verificação
            </h2>
            <p className="text-muted-foreground mb-8">
              {message || 'O link de verificação é inválido ou expirou.'}
            </p>
            <div className="space-y-4">
              <Button
                onClick={() => navigate('/resend-verification')}
                data-testid="resend-verification"
                className="w-full bg-ruby hover:bg-ruby-light text-white rounded-full py-6 font-semibold"
              >
                Solicitar novo email
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="w-full text-muted-foreground"
              >
                Voltar ao Login
              </Button>
            </div>
          </>
        );

      case 'no-token':
        return (
          <>
            <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-10 h-10 text-yellow-500" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              Token em falta
            </h2>
            <p className="text-muted-foreground mb-8">
              Parece que o link de verificação está incompleto. 
              Por favor, usa o link completo enviado para o teu email.
            </p>
            <div className="space-y-4">
              <Button
                onClick={() => navigate('/resend-verification')}
                className="w-full bg-ruby hover:bg-ruby-light text-white rounded-full py-6 font-semibold"
              >
                Pedir novo email de verificação
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="w-full text-muted-foreground"
              >
                Voltar ao Login
              </Button>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=1920)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background" />
        <div className="absolute inset-0 bg-black/40" />
        
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h2 className="font-brand text-4xl font-bold text-foreground mb-4">
            Verificação de Email
          </h2>
          <p className="text-muted-foreground text-lg max-w-md">
            Confirma o teu email para desbloqueares todas as funcionalidades do Intimate.pt.
          </p>
        </div>
      </div>

      {/* Right - Content */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md text-center">
          {/* Logo */}
          <Link to="/" className="block mb-8">
            <img src="/logo-red.png" alt="Intimate.pt" className="h-10 w-auto" />
          </Link>

          {renderContent()}
        </div>
      </div>
    </div>
  );
}
